import { Chip, Button } from "@mui/material";

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR", // Change as needed
  }).format(amount);
};

// Status badge colors
const statusColors = {
  SUCCESS: "success",
  PENDING: "warning",
  FAILED: "error",
  CANCELLED: "default",
};

export const teamMemberColumns = [
  {
    header: "Count",
    accessor: "count",
    render: (value, row, index, currentPage, itemsPerPage) => {
      const page = Number(currentPage) || 1;
      const limit = Number(itemsPerPage) || 10;
      const idx = Number(index);
      return (page - 1) * limit + idx + 1;
    },
  },
  {
    header: "Image",
    accessor: "image",
    render: (value) =>
      value ? (
        <img src={value} alt="member" width={40} style={{ borderRadius: 4 }} />
      ) : (
        <span style={{ fontSize: "0.85em" }}>No Image</span>
      ),
  },
  {
    header: "Name",
    accessor: "name",
    sortable: true,
    cellStyle: { fontSize: "0.80em" },
  },
  {
    header: "Role",
    accessor: "role",
    sortable: true,
    cellStyle: { fontSize: "0.80em" },
  },
  {
    header: "Status",
    accessor: "status",
    sortable: true,
    render: (value, row, index, page, limit, statusColorMap = {}) => {
      const bgMap = {
        active: "#e6f9ec",
        inactive: "#fffbe6",
        deleted: "#ffeaea",
      };
      const colorMap = {
        active: "#388e3c",
        inactive: "#bfa100",
        deleted: "#d32f2f",
      };
      return (
        <Chip
          label={value ? value.charAt(0).toUpperCase() + value.slice(1) : ""}
          size="small"
          sx={{
            background: bgMap[value] || "#e0e7ef",
            color: colorMap[value] || "#222",
            fontWeight: 600,
            fontSize: "0.80em", // smaller font
            borderRadius: 2,
            height: 22,
          }}
        />
      );
    },
  },
];

export const donorReviewColumns = [
  {
    header: "Count",
    accessor: "count",
    render: (value, row, index, page, limit) => (page - 1) * limit + index + 1,
  },
  {
    header: "Image",
    accessor: "image",
    render: (value) =>
      value ? (
        <img
          src={value}
          alt="donor"
          width={40}
          style={{ borderRadius: "4px" }}
        />
      ) : (
        <span style={{ fontSize: "0.85em" }}>No Image</span>
      ),
  },
  {
    header: "Donor Name",
    accessor: "donorName",
    sortable: true,
    cellStyle: { fontSize: "0.95em" },
  },
  {
    header: "Organ Donated At",
    accessor: "donationHospitalName",
    sortable: false, // Explicitly set to false
  },
  {
    header: "Location",
    accessor: "location",
    sortable: false, // Explicitly set to false
  },
  {
    header: "Status",
    accessor: "status",
    sortable: true,
    render: (value, row, index, page, limit, statusColorMap = {}) => {
      const bgMap = {
        active: "#e6f9ec",
        inactive: "#fffbe6",
        deleted: "#ffeaea",
      };
      const colorMap = {
        active: "#388e3c",
        inactive: "#bfa100",
        deleted: "#d32f2f",
      };
      return (
        <Chip
          label={value ? value.charAt(0).toUpperCase() + value.slice(1) : ""}
          size="small"
          sx={{
            background: bgMap[value] || "#e0e7ef",
            color: colorMap[value] || "#222",
            fontWeight: 600,
            fontSize: "0.80em", // smaller font
            borderRadius: 2,
            height: 22,
          }}
        />
      );
    },
  },
];

export const organCategoryColumns = [
  {
    header: "Count",
    accessor: "count", // Make sure this matches your data
    render: (value, row, index, page, limit) => (page - 1) * limit + index + 1,
  },
  {
    header: "Image",
    accessor: "image", // Make sure your data has an 'image' property
    render: (value) =>
      value ? (
        <img src={value} alt="category" width={40} />
      ) : (
        <span style={{ fontSize: "0.85em" }}>No Image</span>
      ),
  },
  {
    header: "Name",
    accessor: "name", // Make sure your data has a 'name' property
    sortable: true,
    cellStyle: { fontSize: "0.95em" },
  },
  {
    header: "Count",
    accessor: "count", // Make sure your data has a 'count' property
    sortable: true,
  },
  {
    header: "Status",
    accessor: "status",
    sortable: true,
    render: (value, row, index, page, limit, statusColorMap = {}) => {
      const bgMap = {
        active: "#e6f9ec", // very light green
        inactive: "#fffbe6", // very light yellow
        deleted: "#ffeaea", // very light red
      };
      const colorMap = {
        active: "#388e3c",
        inactive: "#bfa100",
        deleted: "#d32f2f",
      };
      return (
        <Chip
          label={value ? value.charAt(0).toUpperCase() + value.slice(1) : ""}
          size="small"
          sx={{
            background: bgMap[value] || "#e0e7ef",
            color: colorMap[value] || "#222",
            fontWeight: 600,
            fontSize: "0.80em", // smaller font
            borderRadius: 2,
            height: 22,
          }}
        />
      );
    },
  },
];

export const blogsColumns = [
  {
    header: "Count",
    accessor: "count",
    render: (value, row, index, page, limit) => (page - 1) * limit + index + 1,
  },
  {
    header: "Image",
    accessor: "image",
    render: (value) =>
      value ? (
        <img
          src={value}
          alt="blog"
          width={40}
          style={{ borderRadius: "4px" }}
        />
      ) : (
        <span style={{ fontSize: "0.85em" }}>No Image</span>
      ),
  },
  {
    header: "Blog Title",
    accessor: "heading",
    sortable: false,
    cellStyle: { fontSize: "0.95em" },
  },
  {
    header: "Writer Name",
    accessor: "writtenBy",
    sortable: true,
    cellStyle: { fontSize: "0.95em" },
  },
  {
    header: "Created At",
    accessor: "createdAt",
    sortable: true,
    render: (value) => {
      if (!value) return "N/A";
      const date = new Date(value);
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },
  },
  {
    header: "Status",
    accessor: "status",
    sortable: true,
    render: (value, row, index, page, limit, statusColorMap = {}) => {
      const bgMap = {
        active: "#e6f9ec",
        inactive: "#fffbe6",
        deleted: "#ffeaea",
      };
      const colorMap = {
        active: "#388e3c",
        inactive: "#bfa100",
        deleted: "#d32f2f",
      };
      return (
        <Chip
          label={value ? value.charAt(0).toUpperCase() + value.slice(1) : ""}
          size="small"
          sx={{
            background: bgMap[value] || "#e0e7ef",
            color: colorMap[value] || "#222",
            fontWeight: 600,
            fontSize: "0.80em", // smaller font
            borderRadius: 2,
            height: 22,
          }}
        />
      );
    },
  },
];

export const eventColumns = [
  {
    header: "Count",
    accessor: "count",
    render: (value, row, index, page, limit) => (page - 1) * limit + index + 1,
  },
  {
    header: "Name",
    accessor: "name",
    sortable: true,
    cellStyle: { fontSize: "0.95em" },
  },
  {
    header: "Created At",
    accessor: "createdAt",
    sortable: true,
    render: (value) => {
      if (!value) return "N/A";
      const date = new Date(value);
      return date.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  {
    header: "Status",
    accessor: "status",
    sortable: true,
    render: (value, row, index, page, limit, statusColorMap = {}) => {
      const bgMap = {
        active: "#e6f9ec",
        inactive: "#fffbe6",
        deleted: "#ffeaea",
      };
      const colorMap = {
        active: "#388e3c",
        inactive: "#bfa100",
        deleted: "#d32f2f",
      };
      return (
        <Chip
          label={value ? value.charAt(0).toUpperCase() + value.slice(1) : ""}
          size="small"
          sx={{
            background: bgMap[value] || "#e0e7ef",
            color: colorMap[value] || "#222",
            fontWeight: 600,
            fontSize: "0.80em", // smaller font
            borderRadius: 2,
            height: 22,
          }}
        />
      );
    },
  },
  {
    header: "Image",
    accessor: "image",
    render: (value, row) => {
      // If status is deleted, just show a dash
      if (row.status === "deleted") {
        return "-";
      }

      // Otherwise show the View Image button
      return (
        <Button
          variant="outlined"
          size="small"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontSize: "0.75rem", // smaller font
            color: "#3A6FF8",
            borderColor: "#3A6FF8",
            minWidth: 60,
            height: 28,
            px: 1,
            "&:hover": {
              borderColor: "#2857e0",
              backgroundColor: "rgba(58, 111, 248, 0.04)",
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            window.location.href = `/photo-gallery?categoryId=${row._id}&categoryType=eventCategory`;
          }}
        >
          View Image
        </Button>
      );
    },
  },
];

export const pressColumns = [
  {
    header: "Count",
    accessor: "count",
    render: (value, row, index, page, limit) => (page - 1) * limit + index + 1,
  },
  {
    header: "Name",
    accessor: "name",
    sortable: true,
    cellStyle: { fontSize: "0.95em" },
  },
  {
    header: "Created At",
    accessor: "createdAt",
    sortable: true,
    render: (value) => {
      if (!value) return "N/A";
      const date = new Date(value);
      return date.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  {
    header: "Status",
    accessor: "status",
    sortable: true,
    render: (value, row, index, page, limit, statusColorMap = {}) => {
      const bgMap = {
        active: "#e6f9ec",
        inactive: "#fffbe6",
        deleted: "#ffeaea",
      };
      const colorMap = {
        active: "#388e3c",
        inactive: "#bfa100",
        deleted: "#d32f2f",
      };
      return (
        <Chip
          label={value ? value.charAt(0).toUpperCase() + value.slice(1) : ""}
          size="small"
          sx={{
            background: bgMap[value] || "#e0e7ef",
            color: colorMap[value] || "#222",
            fontWeight: 600,
            fontSize: "0.80em", // smaller font
            borderRadius: 2,
            height: 22,
          }}
        />
      );
    },
  },
  {
    header: "Image",
    accessor: "image",
    render: (value, row) => {
      // If status is deleted, just show a dash
      if (row.status === "deleted") {
        return "-";
      }

      // Otherwise show the View Image button
      return (
        <Button
          variant="outlined"
          size="small"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontSize: "0.75rem", // smaller font
            color: "#3A6FF8",
            borderColor: "#3A6FF8",
            minWidth: 60,
            height: 28,
            px: 1,
            "&:hover": {
              borderColor: "#2857e0",
              backgroundColor: "rgba(58, 111, 248, 0.04)",
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            window.location.href = `/photo-gallery?categoryId=${row._id}&categoryType=pressCategory`;
          }}
        >
          View Image
        </Button>
      );
    },
  },
];

export const photoGalleryColumns = [
  {
    header: "Count",
    accessor: "count",
    render: (value, row, index, currentPage, itemsPerPage) => {
      const page = Number(currentPage) || 1;
      const limit = Number(itemsPerPage) || 10;
      const idx = Number(index);
      return (page - 1) * limit + idx + 1;
    },
  },
  {
    header: "Image",
    accessor: "image",
    render: (value) =>
      value ? (
        <img src={value} alt="member" width={40} style={{ borderRadius: 4 }} />
      ) : (
        <span style={{ fontSize: "0.85em" }}>No Image</span>
      ),
  },
  {
    header: "Name",
    accessor: "name",
    sortable: true,
    cellStyle: { fontSize: "0.95em" },
  },
  {
    header: "Status",
    accessor: "status",
    sortable: true,
    render: (value, row, index, page, limit, statusColorMap = {}) => {
      const bgMap = {
        active: "#e6f9ec", // very light green
        inactive: "#fffbe6", // very light yellow
        deleted: "#ffeaea", // very light red
      };
      const colorMap = {
        active: "#388e3c",
        inactive: "#bfa100",
        deleted: "#d32f2f",
      };
      return (
        <Chip
          label={value ? value.charAt(0).toUpperCase() + value.slice(1) : ""}
          size="small"
          sx={{
            background: bgMap[value] || "#e0e7ef",
            color: colorMap[value] || "#222",
            fontWeight: 600,
            fontSize: "0.80em", // smaller font
            borderRadius: 2,
            height: 22,
          }}
        />
      );
    },
  },
];

export const VideoColumns = [
  {
    header: "Count",
    accessor: "count",
    render: (value, row, index, currentPage, itemsPerPage) => {
      const page = Number(currentPage) || 1;
      const limit = Number(itemsPerPage) || 10;
      const idx = Number(index);
      return (page - 1) * limit + idx + 1;
    },
  },
  {
    header: "Video Thumbnail",
    accessor: "youtubeUrl",
    render: (value) => {
      if (!value) return "No Preview";

      // Extract video ID from YouTube URL
      const getYouTubeID = (url) => {
        const regExp =
          /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
        const match = url.match(regExp);
        return match && match[7].length === 11 ? match[7] : false;
      };

      const videoId = getYouTubeID(value);
      if (!videoId) return "Invalid URL";

      // Use YouTube thumbnail
      const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;

      return (
        <img
          src={thumbnailUrl}
          alt="Video thumbnail"
          width={90}
          style={{ borderRadius: "4px", cursor: "pointer" }}
          onClick={() => window.open(value, "_blank")}
        />
      );
    },
  },
  {
    header: "Video Name",
    accessor: "videoName",
    sortable: true,
    cellStyle: { fontSize: "0.95em" },
  },
  {
    header: "YouTube URL",
    accessor: "youtubeUrl",
    sortable: false,
    render: (value) => {
      if (!value) return "Not Available";
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#3A6FF8",
            textDecoration: "none",
            display: "block",
            maxWidth: "250px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value}
        </a>
      );
    },
  },
  {
    header: "Created At",
    accessor: "createdAt",
    sortable: true,
    render: (value) => {
      if (!value) return "N/A";
      const date = new Date(value);
      return date.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
  },
  {
    header: "Status",
    accessor: "status",
    sortable: true,
    render: (value, row, index, page, limit, statusColorMap = {}) => {
      const bgMap = {
        active: "#e6f9ec", // very light green
        inactive: "#fffbe6", // very light yellow
        deleted: "#ffeaea", // very light red
      };
      const colorMap = {
        active: "#388e3c",
        inactive: "#bfa100",
        deleted: "#d32f2f",
      };
      return (
        <Chip
          label={value ? value.charAt(0).toUpperCase() + value.slice(1) : ""}
          size="small"
          sx={{
            background: bgMap[value] || "#e0e7ef",
            color: colorMap[value] || "#222",
            fontWeight: 600,
            fontSize: "0.80em", // smaller font
            borderRadius: 2,
            height: 22,
          }}
        />
      );
    },
  },
];

export const ElectricMediaColumns = [
  {
    header: "Count",
    accessor: "count",
    render: (value, row, index, currentPage, itemsPerPage) => {
      const page = Number(currentPage) || 1;
      const limit = Number(itemsPerPage) || 10;
      const idx = Number(index);
      return (page - 1) * limit + idx + 1;
    },
  },
  {
    header: "Video Thumbnail",
    accessor: "youtubeUrl",
    render: (value) => {
      if (!value) return "No Preview";

      // Extract video ID from YouTube URL
      const getYouTubeID = (url) => {
        const regExp =
          /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
        const match = url.match(regExp);
        return match && match[7].length === 11 ? match[7] : false;
      };

      const videoId = getYouTubeID(value);
      if (!videoId) return "Invalid URL";

      // Use YouTube thumbnail
      const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;

      return (
        <img
          src={thumbnailUrl}
          alt="Video thumbnail"
          width={90}
          style={{ borderRadius: "4px", cursor: "pointer" }}
          onClick={() => window.open(value, "_blank")}
        />
      );
    },
  },
  {
    header: "Donor Name",
    accessor: "name",
    sortable: true,
    cellStyle: { fontSize: "0.95em" },
  },
  {
    header: "Channel Name",
    accessor: "channelName",
    sortable: true,
    cellStyle: { fontSize: "0.95em" },
  },
  {
    header: "YouTube URL",
    accessor: "youtubeUrl",
    sortable: false,
    render: (value) => {
      if (!value) return "Not Available";
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#1976d2", textDecoration: "underline" }}
        >
          View Video
        </a>
      );
    },
  },
  {
    header: "Date",
    accessor: "date",
    sortable: true,
    render: (value) => {
      if (!value) return "Not Available";
      return new Date(value).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
  {
    header: "Status",
    accessor: "status",
    sortable: true,
    render: (value, row, index, page, limit, statusColorMap = {}) => {
      const bgMap = {
        active: "#e6f9ec", // very light green
        inactive: "#fffbe6", // very light yellow
        deleted: "#ffeaea", // very light red
      };
      const colorMap = {
        active: "#388e3c",
        inactive: "#bfa100",
        deleted: "#d32f2f",
      };
      return (
        <Chip
          label={value ? value.charAt(0).toUpperCase() + value.slice(1) : ""}
          size="small"
          sx={{
            background: bgMap[value] || "#e0e7ef",
            color: colorMap[value] || "#222",
            fontWeight: 600,
            fontSize: "0.80em", // smaller font
            borderRadius: 2,
            height: 22,
          }}
        />
      );
    },
  },
];

export const DigitalMediaColumns = [
  {
    header: "Count",
    accessor: "count",
    render: (value, row, index, currentPage, itemsPerPage) => {
      const page = Number(currentPage) || 1;
      const limit = Number(itemsPerPage) || 10;
      const idx = Number(index);
      return (page - 1) * limit + idx + 1;
    },
  },
  {
    header: "Video Thumbnail",
    accessor: "youtubeUrl",
    render: (value) => {
      if (!value) return "No Preview";

      // Extract video ID from YouTube URL
      const getYouTubeID = (url) => {
        const regExp =
          /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
        const match = url.match(regExp);
        return match && match[7].length === 11 ? match[7] : false;
      };

      const videoId = getYouTubeID(value);
      if (!videoId) return "Invalid URL";

      // Use YouTube thumbnail
      const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;

      return (
        <img
          src={thumbnailUrl}
          alt="Video thumbnail"
          width={90}
          style={{ borderRadius: "4px", cursor: "pointer" }}
          onClick={() => window.open(value, "_blank")}
        />
      );
    },
  },
  {
    header: "Donor Name",
    accessor: "name",
    sortable: true,
    cellStyle: { fontSize: "0.95em" },
  },
  {
    header: "Channel Name",
    accessor: "channelName",
    sortable: true,
    cellStyle: { fontSize: "0.95em" },
  },
  {
    header: "YouTube URL",
    accessor: "youtubeUrl",
    sortable: false,
    render: (value) => {
      if (!value) return "Not Available";
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#1976d2", textDecoration: "underline" }}
        >
          View Video
        </a>
      );
    },
  },
  {
    header: "Date",
    accessor: "date",
    sortable: true,
    render: (value) => {
      if (!value) return "Not Available";
      return new Date(value).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
  {
    header: "Status",
    accessor: "status",
    sortable: true,
    render: (value, row, index, page, limit, statusColorMap = {}) => {
      const bgMap = {
        active: "#e6f9ec", // very light green
        inactive: "#fffbe6", // very light yellow
        deleted: "#ffeaea", // very light red
      };
      const colorMap = {
        active: "#388e3c",
        inactive: "#bfa100",
        deleted: "#d32f2f",
      };
      return (
        <Chip
          label={value ? value.charAt(0).toUpperCase() + value.slice(1) : ""}
          size="small"
          sx={{
            background: bgMap[value] || "#e0e7ef",
            color: colorMap[value] || "#222",
            fontWeight: 600,
            fontSize: "0.80em", // smaller font
            borderRadius: 2,
            height: 22,
          }}
        />
      );
    },
  },
];

export const DonorApplicationColumns = [
  {
    header: "Count",
    accessor: "count",
    render: (value, row, index, currentPage, itemsPerPage) => {
      const page = Number(currentPage) || 1;
      const limit = Number(itemsPerPage) || 10;
      const idx = Number(index);
      return (page - 1) * limit + idx + 1;
    },
  },
  {
    header: "Full Name",
    accessor: "fullName",
    sortable: true,
    cellStyle: { fontSize: "0.95em" },
  },
  {
    header: "Mobile Number",
    accessor: "mobileNumber",
    sortable: true,
    cellStyle: { fontSize: "0.95em" },
  },
  {
    header: "Email",
    accessor: "emailId",
    sortable: true,
    render: (value) => {
      if (!value) return "Not Available";
      return (
        <span style={{ fontSize: "0.875rem" }}>
          {value.length > 25 ? `${value.substring(0, 25)}...` : value}
        </span>
      );
    },
  },
  {
    header: "Application Date",
    accessor: "applicationDate",
    sortable: true,
    render: (value) => {
      if (!value) return "Not Available";
      return new Date(value).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
  {
    header: "Status",
    accessor: "status",
    sortable: true,
    render: (value, row, index, page, limit, statusColorMap = {}) => {
      const bgMap = {
        pending: "#fff3cd", // light yellow
        approved: "#e6f9ec", // very light green
        rejected: "#ffeaea", // very light red
        active: "#e6f9ec", // very light green
        inactive: "#fffbe6", // very light yellow
        deleted: "#ffeaea", // very light red
      };
      const colorMap = {
        pending: "#856404", // dark yellow
        approved: "#388e3c",
        rejected: "#d32f2f",
        active: "#388e3c",
        inactive: "#bfa100",
        deleted: "#d32f2f",
      };
      return (
        <Chip
          label={value ? value.charAt(0).toUpperCase() + value.slice(1) : ""}
          size="small"
          sx={{
            background: bgMap[value] || "#e0e7ef",
            color: colorMap[value] || "#222",
            fontWeight: 600,
            fontSize: "0.80em", // smaller font
            borderRadius: 2,
            height: 22,
          }}
        />
      );
    },
  },
  {
    header: "Approval Status",
    accessor: "actions",
    sortable: false,
    render: (
      value,
      row,
      index,
      page,
      limit,
      statusColorMap,
      extraData = {}
    ) => {
      // Only show approve/reject buttons for pending applications
      if (row.status === "pending") {
        return (
          <div style={{ display: "flex", gap: "8px" }}>
            <Button
              variant="contained"
              size="small"
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontSize: "0.75rem",
                minWidth: "70px",
                backgroundColor: "#4caf50",
                color: "white",
                "&:hover": {
                  backgroundColor: "#45a049",
                },
              }}
              onClick={(e) => {
                console.log(e, "====e");
                e.stopPropagation();
                if (extraData?.onApproveClick) {
                  extraData.onApproveClick(row);
                }
              }}
            >
              Approve
            </Button>
            <Button
              variant="contained"
              size="small"
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontSize: "0.75rem",
                minWidth: "70px",
                backgroundColor: "#f44336",
                color: "white",
                "&:hover": {
                  backgroundColor: "#d32f2f",
                },
              }}
              onClick={(e) => {
                console.log(e, "====e");
                e.stopPropagation();
                if (extraData?.onRejectClick) {
                  extraData.onRejectClick(row);
                }
              }}
            >
              Reject
            </Button>
          </div>
        );
      }
      return null;
    },
  },
];

export const PaymentColumns = [
  {
    header: "Order ID",
    accessor: "orderId",
    sortable: true,
  },
  {
    header: "Customer Name",
    accessor: "customerName",
    sortable: true,
  },
  {
    header: "Amount",
    accessor: "amount",
    sortable: true,
    cell: (row) => formatCurrency(row.amount),
  },
  {
    header: "Status",
    accessor: "status",
    sortable: true,
    cell: (row) => (
      <Chip
        label={row.status}
        color={statusColors[row.status] || "default"}
        size="small"
      />
    ),
  },
  {
    header: "Payment Mode",
    accessor: "paymentMode",
    cell: (row) => row.paymentMode || "N/A",
  },
  {
    header: "Transaction Date",
    accessor: "createdAt",
    sortable: true,
    cell: (row) => formatDate(row.createdAt),
  },
];
